import errorHandler from './errorHandler';

/**
 * 浏览器缓存工具类
 * 用于缓存API数据和图片，避免频繁请求
 */
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 60 * 1000; // 默认1分钟缓存
    this.imageTTL = 24 * 60 * 60 * 1000; // 图片缓存1天
  }

  /**
   * 设置缓存
   * @param {string} key 缓存键
   * @param {any} data 缓存数据
   * @param {number} ttl 过期时间(毫秒)，默认1分钟
   */
  set(key, data, ttl = this.defaultTTL) {
    const expireTime = Date.now() + ttl;
    this.cache.set(key, {
      data: data,
      expireTime: expireTime
    });
    console.log(`缓存设置: ${key}, 过期时间: ${new Date(expireTime).toLocaleTimeString()}`);
  }

  /**
   * 获取缓存
   * @param {string} key 缓存键
   * @returns {any|null} 缓存数据或null
   */
  get(key) {
    const item = this.cache.get(key);
    if (!item) {
      console.log(`缓存未找到: ${key}`);
      return null;
    }

    // 检查是否过期
    if (Date.now() > item.expireTime) {
      this.cache.delete(key);
      console.log(`缓存已过期: ${key}`);
      return null;
    }

    console.log(`缓存命中: ${key}`);
    return item.data;
  }

  /**
   * 删除缓存
   * @param {string} key 缓存键
   */
  delete(key) {
    this.cache.delete(key);
    console.log(`缓存已删除: ${key}`);
  }

  /**
   * 清空所有缓存
   */
  clear() {
    this.cache.clear();
    console.log('所有缓存已清空');
  }

  /**
   * 清理过期缓存
   */
  cleanExpired() {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expireTime) {
        this.cache.delete(key);
        console.log(`清理过期缓存: ${key}`);
      }
    }
  }

  /**
   * 图片缓存相关方法
   */
  
  /**
   * 缓存图片到本地存储
   * @param {string} imageUrl 图片URL
   * @param {string} base64Data 图片的base64数据
   */
  setImageCache(imageUrl, base64Data) {
    const cacheKey = `image_${this.hashUrl(imageUrl)}`;
    const cacheData = {
      url: imageUrl,
      data: base64Data,
      expireTime: Date.now() + this.imageTTL
    };
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log(`图片已缓存: ${imageUrl}`);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('存储空间不足，清理旧缓存');
        this.clearOldImageCache();
        // 重试一次
        try {
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          console.log(`图片已缓存(重试): ${imageUrl}`);
        } catch (retryError) {
          console.error('图片缓存重试失败:', retryError);
        }
      } else {
        console.error('图片缓存失败:', error);
      }
    }
  }
  
  /**
   * 从本地存储获取缓存的图片
   * @param {string} imageUrl 图片URL
   * @returns {string|null} 图片的base64数据或null
   */
  getImageCache(imageUrl) {
    const cacheKey = `image_${this.hashUrl(imageUrl)}`;
    
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) {
        return null;
      }
      
      const cacheData = JSON.parse(cached);
      
      // 检查是否过期
      if (Date.now() > cacheData.expireTime) {
        localStorage.removeItem(cacheKey);
        console.log(`图片缓存已过期: ${imageUrl}`);
        return null;
      }
      
      console.log(`图片缓存命中: ${imageUrl}`);
      return cacheData.data;
    } catch (error) {
      console.error('获取图片缓存失败:', error);
      return null;
    }
  }
  
  /**
   * 加载图片（优先从缓存，没有则网络获取）
   * @param {string} imageUrl 图片URL
   * @param {number} retryCount 重试次数
   * @returns {Promise<string>} 返回图片的base64数据
   */
  async loadImage(imageUrl, retryCount = 3) {
    // 先尝试从缓存获取
    const cachedData = this.getImageCache(imageUrl);
    if (cachedData) {
      return cachedData;
    }
    
    // 缓存中没有，从网络获取
    for (let attempt = 1; attempt <= retryCount; attempt++) {
      try {
        console.log(`尝试加载图片 (第${attempt}次): ${imageUrl}`);
        
        // 添加超时控制
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10秒超时
        
        const response = await fetch(imageUrl, {
          signal: controller.signal,
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Accept': 'image/*',
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        // 检查blob是否有效
        if (blob.size === 0) {
          throw new Error('Image data is empty');
        }
        
        const base64 = await this.blobToBase64(blob);
        
        // 缓存图片
        this.setImageCache(imageUrl, base64);
        
        console.log(`图片加载成功: ${imageUrl}`);
        return base64;
        
      } catch (error) {
        console.warn(`图片加载失败 (第${attempt}次): ${imageUrl}`, error.message);
        
        // 使用错误处理器处理错误
        errorHandler.handleImageError(error, imageUrl);
        
        // 如果是最后一次尝试，返回fallback
        if (attempt === retryCount) {
          console.error('图片加载最终失败:', error);
          return this.getFallbackImage(imageUrl);
        }
        
        // 等待一段时间后重试
        await this.delay(1000 * attempt);
      }
    }
  }
  
  /**
   * 获取备用图片
   * @param {string} imageUrl 原图片URL
   * @returns {string} 备用图片URL或base64
   */
  getFallbackImage(imageUrl) {
    // 检查是否是特定域名的图片，提供不同的备用方案
    if (imageUrl.includes('ma.xexexoox10svip1x.com')) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L295LitLi4uPC90ZXh0Pjwvc3ZnPg==';
    }
    
    if (imageUrl.includes('admxo.leyou-1.com')) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L295LitLi4uPC90ZXh0Pjwvc3ZnPg==';
    }
    
    // 默认返回原URL
    return imageUrl;
  }
  
  /**
   * 延迟函数
   * @param {number} ms 延迟毫秒数
   * @returns {Promise}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  /**
   * 将Blob转换为Base64
   * @param {Blob} blob 
   * @returns {Promise<string>}
   */
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  
  /**
   * 生成URL的哈希值
   * @param {string} url 
   * @returns {string}
   */
  hashUrl(url) {
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      const char = url.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32位整数
    }
    return Math.abs(hash).toString(36);
  }
  
  /**
   * 清理过期的图片缓存
   */
  cleanExpiredImages() {
    const now = Date.now();
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('image_')) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (now > cached.expireTime) {
            keysToRemove.push(key);
          }
        } catch (error) {
          // 解析失败，删除
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`清理过期图片缓存: ${key}`);
    });
  }

  /**
   * 获取缓存状态
   */
  getStatus() {
    const now = Date.now();
    const status = {
      total: this.cache.size,
      valid: 0,
      expired: 0,
      items: [],
      imageCache: {
        total: 0,
        valid: 0,
        expired: 0
      }
    };

    // 内存缓存状态
    for (const [key, item] of this.cache.entries()) {
      const isExpired = now > item.expireTime;
      if (isExpired) {
        status.expired++;
      } else {
        status.valid++;
      }
      
      status.items.push({
        key: key,
        expireTime: new Date(item.expireTime).toLocaleTimeString(),
        isExpired: isExpired
      });
    }
    
    // 图片缓存状态
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('image_')) {
        status.imageCache.total++;
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (now > cached.expireTime) {
            status.imageCache.expired++;
          } else {
            status.imageCache.valid++;
          }
        } catch (error) {
          status.imageCache.expired++;
        }
      }
    }

    return status;
  }
  
  /**
   * 清理过期的图片缓存
   */
  clearOldImageCache() {
    const now = Date.now();
    const keysToRemove = [];
    
    // 遍历所有localStorage键
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('image_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.expireTime && data.expireTime < now) {
            keysToRemove.push(key);
          }
        } catch (error) {
          // 如果解析失败，也删除这个键
          keysToRemove.push(key);
        }
      }
    }
    
    // 删除过期的键
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log(`清理了 ${keysToRemove.length} 个过期图片缓存`);
  }
}

// 创建全局缓存实例
const cacheManager = new CacheManager();

// 定期清理过期缓存（每5分钟）
setInterval(() => {
  cacheManager.cleanExpired();
  cacheManager.cleanExpiredImages();
}, 5 * 60 * 1000);

export default cacheManager;
